import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { newsAPI, adsAPI, imagaeAPI } from '../../services/api';
import Header from './Header';
import Footer from './Footer';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import 'react-toastify/dist/ReactToastify.css';

const NewsDetailsKPTV = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // State
    const [newsItem, setNewsItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ads, setAds] = useState([]);
    const [temperature, setTemperature] = useState('Loading...');

    // Fetch ads
    const fetchAds = async () => {
        try {
            const res = await adsAPI.getAll();
            setAds(res.data.data);
        } catch (err) {
            console.error('Error fetching ads:', err);
        }
    };

    // Fetch single news by ID
    const fetchNewsById = async (id) => {
        setLoading(true);
        try {
            const res = await newsAPI.getById(id); // make sure this method exists
            setNewsItem(res.data.data);
            setError(null);
        } catch (err) {
            console.error(err);
            setError('Failed to load news details.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Get temperature
        fetch('https://wttr.in/Bengaluru?format=j1')
            .then((res) => res.json())
            .then((data) => {
                setTemperature(data.current_condition[0].temp_C + '°C');
            })
            .catch(() => setTemperature('N/A'));

        fetchAds();

        // Determine what was passed via state
        const stateData = location.state?.id;

        if (!stateData) {
            navigate('/'); // redirect if no data
            return;
        }

        // If it's already a full object (e.g., RSS feed item)
        if (typeof stateData === 'object' && stateData.title) {
            setNewsItem(stateData);
            setLoading(false);
            return;
        }

        // If it's a number, fetch from API
        if (typeof stateData === 'number' || !isNaN(stateData)) {
            fetchNewsById(Number(stateData));
        } else {
            setError('Invalid news data.');
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state, navigate]);

    // ---- Loading ----
    if (loading) {
        return (
            <div className="main-content" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    // ---- Error or no data ----
    if (error || !newsItem) {
        return (
            <>
                <Header temperature={temperature} />
                <main>
                    <div className="container" style={{ padding: '50px 0' }}>
                        <div className="alert alert-danger">{error || 'News not found'}</div>
                    </div>
                </main>
                <Footer />
            </>
        );
    }

    // ---- Now we have data – safely extract fields ----
    const title = newsItem.news_title || newsItem.title || 'Untitled';
    const image = newsItem.photo
        ? `${imagaeAPI}/uploads/news/${newsItem.photo}`
        : (newsItem.image || 'assets/img/blog/noimage.jpg');
    const date = newsItem.created_at || newsItem.pubDate;
    const description = newsItem.news_content || newsItem.description || 'No description available.';
    const originalLink = newsItem.link || null;

    const categories = newsItem.categories || null;

    const formattedDate = date
        ? new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
        : 'Date not available';

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />
            <Header temperature={temperature} />

            <main>
                {/* Breadcrumb */}
                <section className="breadcrumb-section__area">
                    <div className="container">
                        <div className="breadcrumb-section__content">
                            <h2 className="breadcrumb-section__title"><span
                                dangerouslySetInnerHTML={{ __html: title }}
                            /></h2>
                            <ul className="breadcrumb-section__list">
                                <li><Link to="/"><i className="fa-regular fa-house" /> Home</Link></li>

                            </ul>
                        </div>
                    </div>
                </section>

                {/* News details */}
                <section className="news-details__area grey-bg section-space">
                    <div className="container">
                        <div className="row mb-minus-20">
                            <div className="col-lg-8">
                                <div className="blog-details__wrapper">
                                    <div className="blog-details__card">
                                        {image && (
                                            <div className="blog-details__thumb">
                                                <img src={image} alt={title} className="img-fluid" />
                                            </div>
                                        )}
                                        <div className="blog-details__content">


                                            {categories !== "Interviews" && (
                                                <div className="blog-details__meta" style={{ marginBottom: '1.5rem' }}>
                                                    <span className="date">
                                                        <i className="fa-regular fa-calendar" /> {formattedDate}
                                                    </span>
                                                </div>
                                            )}

                                            <div className="quill-readonly">
                                                <ReactQuill
                                                    theme="snow"
                                                    value={description}
                                                    readOnly={true}
                                                    modules={{ toolbar: false }}
                                                />
                                            </div>

                                            {originalLink && (
                                                <a href={originalLink} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                                                    Read Full Article
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Sidebar Ads */}
                            <div className="col-lg-4">
                                <div className="blog-sidebar__wrapper">
                                    <div className="blog-sidebar__wrap mb-20">
                                        <h3 className="blog-sidebar__title">Advertisement</h3>
                                        <div className="blog-sidebar__gallery blog-sidebar__gallery-2">
                                            {ads.length > 0 && ads.map((ad) => (
                                                <div key={ad.id}>
                                                    <img src={`${imagaeAPI}/uploads/ads/${ad.ads_photo}`} alt={ad.ads_title} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </>
    );
};

export default NewsDetailsKPTV;